import React, { useState, useCallback, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { Loader2, Rocket } from 'lucide-react';
import { Panel, Group as PanelGroup, Separator as PanelResizeHandle } from 'react-resizable-panels';
import { CopilotKit } from '@copilotkit/react-core';
import { apiClient } from '@/services/api';
import { useIDEStore } from '@/stores/useIDEStore';
import { useTerminalStore } from '@/stores/useTerminalStore';
import { Button } from '@/components/ui/button';
import { toast } from 'sonner';

import IDEHeader from '@/components/ide/IDEHeader';
import FileExplorer from '@/components/ide/FileExplorer';
import EditorTabs from '@/components/ide/EditorTabs';
import MonacoEditorWrapper from '@/components/ide/MonacoEditorWrapper';
import TerminalPanel from '@/components/ide/TerminalPanel';
import PreviewPanel from '@/components/ide/PreviewPanel';
import AIChatPanel from '@/components/ide/AIChatPanel';
import VisualBuilderEmbed from '@/components/ide/VisualBuilderEmbed';

export default function IDELayout() {
  const { applicationId } = useParams<{ applicationId: string }>();
  const { mode, panelVisibility } = useIDEStore();
  const { sandboxId } = useTerminalStore();
  const [previewUrl, setPreviewUrl] = useState<string | null>(null);

  // Warn user about unsaved changes before navigating away
  useEffect(() => {
    const handleBeforeUnload = (e: BeforeUnloadEvent) => {
      const hasDirtyFiles = useIDEStore.getState().openFiles.some((f) => f.isDirty);
      if (hasDirtyFiles) {
        e.preventDefault();
      }
    };
    window.addEventListener('beforeunload', handleBeforeUnload);
    return () => window.removeEventListener('beforeunload', handleBeforeUnload);
  }, []);

  const { data: application, isLoading } = useQuery({
    queryKey: ['application', applicationId],
    queryFn: () => apiClient.getApplication(applicationId!),
    enabled: !!applicationId,
  });

  const handleRun = useCallback(async () => {
    if (!applicationId) return;
    try {
      const API_URL = import.meta.env.VITE_API_URL || 'http://localhost:3000/api';
      const token = localStorage.getItem('access_token');

      // Create sandbox if not exists
      let currentSandboxId = sandboxId;
      if (!currentSandboxId) {
        const res = await fetch(`${API_URL}/sandboxes/${applicationId}/create`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${token}`,
          },
        });
        if (!res.ok) throw new Error('Failed to create sandbox');
        const data = await res.json();
        currentSandboxId = data.id;
        useTerminalStore.getState().setSandboxId(data.id);
        useTerminalStore.getState().setConnected(true);
      }

      // Get preview URL
      const previewRes = await fetch(`${API_URL}/sandboxes/${currentSandboxId}/preview-url`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (previewRes.ok) {
        const previewData = await previewRes.json();
        setPreviewUrl(previewData.previewUrl || previewData.url);
      }

      toast.success('Sandbox started');
    } catch (error) {
      toast.error(error instanceof Error ? error.message : 'Failed to start sandbox');
    }
  }, [applicationId, sandboxId]);

  const handleDeploy = useCallback(async () => {
    if (!applicationId) return;
    try {
      await apiClient.deployApplication(applicationId);
      toast.success('Deployment started');
    } catch (error) {
      toast.error(error instanceof Error ? error.message : 'Failed to deploy');
    }
  }, [applicationId]);

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-screen">
        <Loader2 className="h-12 w-12 animate-spin text-primary" />
      </div>
    );
  }

  if (!application || !applicationId) {
    return (
      <div className="flex items-center justify-center h-screen">
        <div className="text-center">
          <Rocket className="h-12 w-12 mx-auto text-muted-foreground mb-4" />
          <h2 className="text-xl font-semibold mb-2">Application not found</h2>
          <Button onClick={() => window.history.back()}>Go Back</Button>
        </div>
      </div>
    );
  }

  // Visual mode — show the embedded visual builder
  if (mode === 'visual') {
    return (
      <div className="h-screen flex flex-col">
        <IDEHeader
          projectName={application.name}
          projectId={applicationId}
          onRun={handleRun}
          onDeploy={handleDeploy}
        />
        <div className="flex-1">
          <VisualBuilderEmbed projectId={applicationId} />
        </div>
      </div>
    );
  }

  const runtimeUrl = `${import.meta.env.VITE_API_URL || 'http://localhost:3000/api'}/copilot`;

  // Code mode — IDE layout with resizable panels
  return (
    <CopilotKit runtimeUrl={runtimeUrl} properties={{ projectId: applicationId }}>
      <div className="h-screen flex flex-col bg-background">
        <IDEHeader
          projectName={application.name}
          projectId={applicationId}
          onRun={handleRun}
          onDeploy={handleDeploy}
        />

        <div className="flex-1 min-h-0">
          <PanelGroup orientation="horizontal">
            {/* File Explorer */}
            {panelVisibility.fileExplorer && (
              <>
                <Panel defaultSize={15} minSize={10} maxSize={25}>
                  <FileExplorer projectId={applicationId} />
                </Panel>
                <PanelResizeHandle className="w-1 bg-border hover:bg-primary/50 transition-colors" />
              </>
            )}

            {/* Editor + Terminal (center) */}
            <Panel defaultSize={panelVisibility.preview || panelVisibility.aiChat ? 45 : 85} minSize={30}>
              <PanelGroup orientation="vertical">
                {/* Editor area */}
                <Panel defaultSize={panelVisibility.terminal ? 65 : 100} minSize={30}>
                  <div className="h-full flex flex-col">
                    <EditorTabs />
                    <div className="flex-1 min-h-0">
                      <MonacoEditorWrapper projectId={applicationId} />
                    </div>
                  </div>
                </Panel>

                {/* Terminal */}
                {panelVisibility.terminal && (
                  <>
                    <PanelResizeHandle className="h-1 bg-border hover:bg-primary/50 transition-colors" />
                    <Panel defaultSize={35} minSize={15} maxSize={60}>
                      <TerminalPanel sandboxId={sandboxId} />
                    </Panel>
                  </>
                )}
              </PanelGroup>
            </Panel>

            {/* Preview */}
            {panelVisibility.preview && (
              <>
                <PanelResizeHandle className="w-1 bg-border hover:bg-primary/50 transition-colors" />
                <Panel defaultSize={20} minSize={15} maxSize={40}>
                  <PreviewPanel previewUrl={previewUrl} />
                </Panel>
              </>
            )}

            {/* AI Chat */}
            {panelVisibility.aiChat && (
              <>
                <PanelResizeHandle className="w-1 bg-border hover:bg-primary/50 transition-colors" />
                <Panel defaultSize={20} minSize={15} maxSize={35}>
                  <AIChatPanel projectId={applicationId} />
                </Panel>
              </>
            )}
          </PanelGroup>
        </div>
      </div>
    </CopilotKit>
  );
}
